/* eslint-disable react/no-did-update-set-state */
import React from 'react';
import PropTypes from 'prop-types';
import flatMap from 'lodash/flatMap';
import {
  Wrapper,
  ChildrenWrapper,
  ParentWrapper,
  ParentItem,
  ChildrenItem,
  NonInteractiveItem,
  SearchInput,
  CheckIcon,
  ScrollWrapper,
  FilterWrapperGhost,
  FilterWrapper,
  ClearButton,
  ItemIcon,
  GroupItem,
} from './styles';
import PulseCheckLogo from '../../PulseCheck/img/tile-logo.svg';

const getCustomIcon = icon => {
  if (icon === 'pulseCheck') {
    return (
      <PulseCheckLogo
        width={20}
        css={`
          color: ${({ theme }) => theme.colors.neutral['500']};
          margin-bottom: -4px;
          margin-right: 6px;
        `}
      />
    );
  }

  return null;
};

export const getItemsFlat = items =>
  flatMap(items, item => (Array.isArray(item.items) ? [item, ...item.items] : [item]));

class Dropdown extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      query: '',
      children: [],
      childrenPosition: {},
      parentPosition: {},
      isVisible: false,
      isOpenUp: false,
      isChildrenOpenUp: false,
    };

    // Use this tolerance for number comparisons
    this.TOLERANCE = 0.001;

    this.handleOnMouseEnter = this.handleOnMouseEnter.bind(this);
    this.handleOnMouseLeave = this.handleOnMouseLeave.bind(this);
    this.handleChildrenMouseLeave = this.handleChildrenMouseLeave.bind(this);
    this.showDropdown = this.showDropdown.bind(this);
    this.hideDropdown = this.hideDropdown.bind(this);
    this.filteredItems = this.filteredItems.bind(this);
  }

  componentDidUpdate() {
    const { isOpenUp, isChildrenOpenUp, children, childrenPosition, parentPosition } = this.state;
    if (this.dropdownRef && !isOpenUp) {
      const { bottom, right, height } = this.dropdownRef.getBoundingClientRect();

      const {
        height: wrapperHeight,
        left: wrapperLeft,
        bottom: wrapperBottom,
        right: wrapperRight,
        top: wrapperTop,
      } = this.wrapperRef.getBoundingClientRect();

      // The dropdown would be beyond the bottom edge of the screen
      // ➜ Render it up
      if (bottom - window.innerHeight > this.TOLERANCE) {
        this.setState({
          isOpenUp: true,
          parentPosition: {
            bottom: window.innerHeight - (wrapperBottom - wrapperHeight),
            left: wrapperLeft,
            top: wrapperTop - height,
          },
        });
      }
      // The dropdown toggle is beyond the window width
      // ➜ Render the dropdown flushed to the right edge of the screen
      if (wrapperRight - window.innerWidth > this.TOLERANCE) {
        // If the dropdown is already flushed return
        // (prevent infinite loop)
        if (parentPosition.right === 0) {
          return;
        }
        this.setState(prevState => ({
          parentPosition: {
            ...prevState.parentPosition,
            right: 0,
            left: 'auto',
          },
        }));
      } else if (right - window.innerWidth > this.TOLERANCE) {
        // The dropdown would be beyond the window right edge
        // Render the dropdown flushed with the right edge of the
        // wrapper.
        this.setState(prevState => ({
          parentPosition: {
            ...prevState.parentPosition,
            right: window.innerWidth - wrapperRight,
            left: 'auto',
          },
        }));
      }
    }

    if (this.parentRef && !isChildrenOpenUp && children.length > 0) {
      const {
        bottom: childrenWrapperBottom,
        height: childrenWrapperHeight,
      } = this.childrenWrapperRef.getBoundingClientRect();

      const { left, width, bottom, top } = this.parentRef.getBoundingClientRect();

      const halfChildrenWrapperHeight = childrenWrapperHeight / 2;
      const isChildrenWrapperOverflowBrowserHeight = childrenWrapperBottom > window.innerHeight;
      const isChildrenHalfWrapperOverflowBrowserHeight = Math.ceil(
        window.innerHeight - bottom - halfChildrenWrapperHeight,
      );

      if (isChildrenWrapperOverflowBrowserHeight && isChildrenHalfWrapperOverflowBrowserHeight) {
        this.setState({
          isChildrenOpenUp: true,
          childrenPosition: {
            left: left + width,
            top: 'auto',
            bottom: window.innerHeight - bottom,
          },
        });
      } else if (
        isChildrenWrapperOverflowBrowserHeight &&
        !isChildrenHalfWrapperOverflowBrowserHeight
      ) {
        this.setState({
          isChildrenOpenUp: true,
          childrenPosition: {
            left: left + width,
            top: 'auto',
            bottom: Math.ceil(window.innerHeight - bottom - halfChildrenWrapperHeight),
          },
        });
      } else if (
        childrenPosition.top === undefined ||
        Math.abs(childrenPosition.top - top + halfChildrenWrapperHeight) > this.TOLERANCE
      ) {
        // If we reach this, the children menu is not overflowing the browser height
        // We set the top value if it is either not set yet or if it is not equal
        // to the desired value (we use a tolerance for number comparisons ➜ ADV-7606).
        // Note: We need the check to prevent an infinite loop.
        this.setState({
          childrenPosition: {
            left: left + width,
            top: top - halfChildrenWrapperHeight,
            bottom: 'auto',
          },
        });
      }
    }
  }

  showDropdown() {
    const { onShow } = this.props;
    const {
      height: wrapperHeight,
      left: wrapperLeft,
      top: wrapperTop,
    } = this.wrapperRef.getBoundingClientRect();

    this.setState({
      isOpenUp: false,
      isVisible: true,
      parentPosition: {
        top: wrapperTop + wrapperHeight,
        left: wrapperLeft,
        bottom: 'auto',
      },
    });
    if (onShow) {
      onShow();
    }
  }

  hideDropdown() {
    const { onHide } = this.props;
    this.setState({
      query: '',
      isVisible: false,
      parentPosition: {},
      children: [],
      isChildrenOpenUp: false,
      isOpenUp: false,
    });
    if (onHide) {
      onHide();
    }
  }

  handleOnMouseEnter(event, items) {
    if (items) {
      this.setState({
        children: items,
        childrenPosition: {},
      });
    }
  }

  handleOnMouseLeave(event) {
    const {
      currentTarget,
      clientX, // Mouse X
      clientY, // Mouse Y
    } = event;

    const { height, left, top } = currentTarget.getBoundingClientRect();

    const parentX = left;
    const parentY = top;

    const isOutsideLeftSideOfParent = clientX < parentX;
    const isOutsideTopSideOfParent = clientY < parentY;
    const isOutsideBottomSideOfParent = clientY >= Math.floor(parentY + height);

    if (isOutsideLeftSideOfParent || isOutsideTopSideOfParent || isOutsideBottomSideOfParent) {
      this.setState({
        isChildrenOpenUp: false,
        children: [],
        childrenPosition: {},
      });
    }
  }

  handleChildrenMouseLeave() {
    this.setState({
      children: [],
      childrenPosition: {},
      isChildrenOpenUp: false,
    });
  }

  filteredItems() {
    const { query } = this.state;
    const { items } = this.props;
    if (query.trim() === '') {
      return items;
    }

    const findNodes = itemsToFilter =>
      getItemsFlat(itemsToFilter).filter(item =>
        item.label.toLowerCase().includes(query.trim().toLowerCase()),
      );

    const result = findNodes(items);

    result.sort((a, b) => {
      const aLower = a.label.toLowerCase();
      const bLower = b.label.toLowerCase();
      const aPrefixed = aLower.startsWith(query.trim().toLowerCase());
      const bPrefixed = bLower.startsWith(query.trim().toLowerCase());
      if (aPrefixed && !bPrefixed) {
        return -1;
      }
      if (!aPrefixed && bPrefixed) {
        return 1;
      }
      return aLower.localeCompare(bLower);
    });

    return result;
  }

  render() {
    const {
      onChange,
      isSearchable,
      value,
      className,
      showFilterHeader,
      children: propsChildren,
      disabled,
      IEProps,
    } = this.props;
    const { isVisible, parentPosition, isOpenUp, query, children, childrenPosition } = this.state;
    const showFilterHeaderWhenFilterIsOn = showFilterHeader && value !== '';

    const isKeyboardNavigation = document.documentElement.dataset.whatinput === 'keyboard';

    return (
      <Wrapper
        className={className}
        ref={wrapper => {
          this.wrapperRef = wrapper;
        }}
        onMouseLeave={this.hideDropdown}
        onMouseEnter={disabled ? () => {} : this.showDropdown}
      >
        <div
          onKeyPress={disabled ? () => {} : this.showDropdown}
          aria-expanded={isVisible}
          role="button"
          tabIndex={0}
        >
          {propsChildren}
        </div>

        <ScrollWrapper showOpenStyles={isVisible} style={parentPosition} isOpenUp={isOpenUp}>
          {isVisible && (
            <ParentWrapper
              IEProps={IEProps}
              ref={dropdown => {
                this.dropdownRef = dropdown;
              }}
            >
              {isSearchable && (
                <NonInteractiveItem>
                  <SearchInput
                    placeholder="Search..."
                    value={query}
                    onChange={e => this.setState({ query: e.target.value })}
                  />
                </NonInteractiveItem>
              )}
              {this.filteredItems().map((item, i, arr) => {
                const customIcon = getCustomIcon(item.icon);

                return (
                  <React.Fragment key={item.value}>
                    {item.groupId && (i === 0 || item.groupId !== arr[i - 1].groupId) && (
                      <GroupItem>{item.groupId}</GroupItem>
                    )}
                    <ParentItem
                      key={item.label}
                      tabIndex="0"
                      onFocus={event => {
                        if (isKeyboardNavigation) {
                          this.parentRef = event.currentTarget;
                          this.handleOnMouseEnter(event, item.items);
                        }
                      }}
                      onKeyboardPress={() => {
                        onChange(item);
                        this.hideDropdown();
                      }}
                      onMouseEnter={event => {
                        this.parentRef = event.currentTarget;
                        this.handleOnMouseEnter(event, item.items);
                      }}
                      onBlur={() => {}}
                      onMouseLeave={this.handleOnMouseLeave}
                      hasChildren={item.items && item.items.length > 0}
                      onClick={() => {
                        onChange(item);
                        this.hideDropdown();
                      }}
                      data-action={item.dataAction}
                      data-params={item.dataParams}
                      role="link"
                    >
                      {item.icon && (customIcon || <ItemIcon className={item.icon} />)}
                      {item.label}
                      {item.value === value && (
                        <CheckIcon hasCaret={item.items && item.items.length} />
                      )}
                    </ParentItem>
                  </React.Fragment>
                );
              })}
              {showFilterHeaderWhenFilterIsOn && <FilterWrapperGhost />}
              {showFilterHeaderWhenFilterIsOn && (
                <FilterWrapper
                  onMouseEnter={() => this.setState({ children: [], childrenPosition: {} })}
                >
                  <ClearButton
                    onClick={() => {
                      onChange({ value: '', label: '' });
                      this.hideDropdown();
                    }}
                  >
                    Clear
                  </ClearButton>
                </FilterWrapper>
              )}
            </ParentWrapper>
          )}
        </ScrollWrapper>
        {isVisible && (
          <ChildrenWrapper
            isOpen={children.length > 0}
            style={childrenPosition}
            onBlur={() => {}}
            onMouseLeave={this.handleChildrenMouseLeave}
            ref={childrenWrapper => {
              this.childrenWrapperRef = childrenWrapper;
            }}
          >
            {children.map(child => (
              <ChildrenItem
                key={child.label}
                tabIndex="0"
                onKeyPress={() => {
                  onChange(child);
                  this.hideDropdown();
                }}
                onClick={() => {
                  onChange(child);
                  this.hideDropdown();
                }}
              >
                {child.icon && <ItemIcon className={child.icon} />}
                {child.label}
                {child.value === value && <CheckIcon />}
              </ChildrenItem>
            ))}
          </ChildrenWrapper>
        )}
      </Wrapper>
    );
  }
}

Dropdown.defaultProps = {
  items: [],
  onChange: () => {},
  onShow: null,
  onHide: null,
  isSearchable: false,
  value: null,
  className: null,
  showFilterHeader: false,
  disabled: false,
  IEProps: {
    showLessOptions: false,
  },
};

Dropdown.propTypes = {
  children: PropTypes.node.isRequired,
  items: PropTypes.arrayOf(PropTypes.shape({})),
  onChange: PropTypes.func,
  onShow: PropTypes.func,
  onHide: PropTypes.func,
  isSearchable: PropTypes.bool,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  className: PropTypes.string,
  showFilterHeader: PropTypes.bool,
  disabled: PropTypes.bool,
  IEProps: PropTypes.shape({
    showLessOptions: PropTypes.bool,
  }),
};

export default Dropdown;
